/**
 * LaunchBoard — the first screen of the Monday weekly plan (Matt, 2026-10-25: "I want to first see the
 * launch board showing target dates with buffer info listed in order of priority for all active
 * projects"). docs/ALDERFLOW.md §5c2-5c3.
 *
 * Every active project counting down in working days to Build Ready, worst first: the projects losing
 * their buffer, then those without a target yet by how far behind they already are. The bar is the
 * buffer (red burned, green left). A project without a target shows its proposed date; the plan's cards
 * set it. Construction is never on the board.
 */
import RoleChip from './RoleChip.jsx'
import { useEffect, useState } from 'react'
import { getBuildReady, setProjectTarget } from '../services/alderflow.js'
import { ctx } from '../outline/ctx.js'

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
const COLOR = { red: '#c0392b', yellow: '#b7791f', green: '#1f7a4d', none: '#8e8e93' }
const LABEL = { red: 'Needs help now', yellow: 'Watch', green: 'On track', none: 'No Build Ready date yet' }

function wd(a, b) { let n = 0, d = new Date(a + 'T12:00:00Z'); const end = new Date(b + 'T12:00:00Z'); while (d < end) { d = new Date(d.getTime() + 86400000); const w = d.getUTCDay(); if (w && w < 6) n++ } return n }

// `embedded` (Flight Board → Reserves): the board alone, without the weekly plan's heading and Start button.
// There `data` comes from the Reserves tab's own live read, and `onChanged` re-reads it after a date is set.
export default function LaunchBoard({ onStart, startLabel = 'Start the plan', embedded = false, data: given, onChanged }) {
  const [own, setData] = useState(null)
  const data = given !== undefined ? given : own
  const [setting, setSetting] = useState('')   // '' | 'all' | projectId
  const [msg, setMsg] = useState('')
  const load = () => getBuildReady().then(d => setData(d || { projects: [] })).catch(() => setData({ projects: [] }))
  useEffect(() => { if (given === undefined) load() }, [])   // eslint-disable-line react-hooks/exhaustive-deps
  const me = ctx.currentMember
  const who = { memberId: me && me.id, memberName: me && me.fields ? (me.fields['Member Name'] || me.fields['Name'] || null) : null }
  // SET THE TARGETS RIGHT HERE (Matt, 2026-10-26: "how do I get these to have a target?"): every project
  // without one takes its proposed date in one click, or one at a time on its tile. Any can be moved later,
  // with a reason, on its project card.
  async function hold(list) {
    setSetting(list.length > 1 ? 'all' : list[0].projectId); setMsg('')
    let ok = 0, failed = 0
    for (const p of list) {
      try { await setProjectTarget({ projectId: p.projectId, projectName: p.name, date: p.proposed, chainDays: p.chainDays, bufferDays: p.bufferDays, basis: { split: p.split, path: p.path, estimated: p.estimated }, reason: null, ...who }); ok++ }
      catch { failed++ }
    }
    setMsg(`${ok} Build Ready date${ok === 1 ? '' : 's'} set${failed ? `; ${failed} could not be saved` : ''}.`)
    setSetting(''); await (onChanged ? onChanged() : load())
  }
  const rows = ((data && data.projects) || []).filter(p => p.eligible && !p.reached).map(p => {
    if (p.target) {
      const pct = p.burn ? Math.min(1, p.burn.pct || 0) : 0
      return { p, zone: p.burn ? (p.burn.zone === 'red' ? 'red' : p.burn.zone === 'yellow' ? 'yellow' : 'green') : 'green', pct, left: p.target.left, date: p.target.date, sub: `${p.chainDays} work + ${p.target.bufferDays} buffer · ${p.burn ? p.burn.burned : 0} burned${p.burn && p.burn.late ? ' · late' : ''}`, rank: 2 + pct }
    }
    const behind = p.overdue ? p.overdue.days : 0
    return { p, zone: 'none', pct: 0, left: data.today ? wd(data.today, p.proposed) : null, date: p.proposed, sub: `${p.chainDays} work + ${p.bufferDays} buffer${behind ? ` · ${behind} days behind already` : ''}`, rank: behind / 1000 }
  }).sort((a, b) => b.rank - a.rank || (a.left || 0) - (b.left || 0))
  const counts = rows.reduce((c, r) => { c[r.zone] = (c[r.zone] || 0) + 1; return c }, {})

  return (
    <div>
      {embedded ? (
        <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', marginBottom: 8 }}>
          <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 4 }}>Every project</div>
          Counting down to Build Ready in working days, worst first. The bar is the buffer: the coloured part is used, its colour the project's status on the fever chart.
        </div>
      ) : (<>
      <RoleChip role="Client Manager" what="The weekly plan for the projects you manage" />
      <div className="aj-pg-kind">This week’s plan</div>
      <h1 className="aj-pg-h1">The launch board</h1>
      <p className="aj-pg-sub">Every active project counting down to Build Ready, in working days, worst first. The bar is the buffer: red is burned, green is left. Hover a project for its work and buffer.</p>
      </>)}
      {data === null ? <div style={S.dim}>Reading every project…</div> : (
        <>
          {(() => {
            const none = ((data && data.projects) || []).filter(p => p.eligible && !p.reached && !p.target && p.proposed)
            if (!none.length) return msg ? <div style={{ ...S.sum, color: '#1f7a4d' }}>{msg}</div> : null
            return (
              <div style={S.setAll}>
                <span>{none.length} project{none.length === 1 ? ' has' : 's have'} no Build Ready date yet.</span>
                <button type="button" style={S.setBtn} disabled={!!setting} onClick={() => hold(none)}>
                  {setting === 'all' ? 'Setting…' : `Set all ${none.length} to their proposed dates`}
                </button>
                {msg && <span style={{ color: '#1f7a4d' }}>{msg}</span>}
              </div>
            )
          })()}
          <div style={S.sum}>{[['red', 'need help now'], ['yellow', 'to watch'], ['green', 'on track'], ['none', 'without a Build Ready date yet']].filter(([z]) => counts[z]).map(([z, w]) => <span key={z} style={{ color: COLOR[z] }}>{counts[z]} {w}</span>)}</div>
          <div style={S.grid}>
            {rows.map(r => (
              <div key={r.p.projectId} style={{ ...S.tile, borderTopColor: r.zone === 'none' ? '#d1d1d6' : COLOR[r.zone] }} title={`${r.p.name} · ${LABEL[r.zone]} · ${r.sub}`}>
                <div style={S.name}>{r.p.name.replace(/ Residence.*$/, '')}</div>
                <div style={{ ...S.t, ...(r.left < 0 ? { color: COLOR.red } : null) }} title={r.left < 0 ? 'Past its Build Ready date by this many working days' : 'Working days to its Build Ready date'}>{r.left < 0 ? `T+${-r.left}` : `T-${r.left}`}</div>
                <div style={S.bar}><i style={{ display: 'block', height: '100%', width: `${r.pct * 100}%`, background: r.zone === 'none' ? COLOR.red : COLOR[r.zone] }} /><i style={{ display: 'block', height: '100%', flex: 1, background: r.zone === 'none' ? '#d1d1d6' : COLOR.green, opacity: 0.6 }} /></div>
                <div style={{ ...S.line, color: r.zone === 'green' || r.zone === 'none' ? '#6e6e73' : COLOR[r.zone] }}>{fmt(r.date)}{r.p.target && r.p.burn ? (r.p.burn.late ? ` · late by ${-r.p.burn.margin} working days` : ` · ${r.p.burn.done || 0}% of the plan done · ${Math.round(r.pct * 100)}% of the buffer used`) : ''}</div>
                {r.zone === 'none' && <button type="button" style={S.tileBtn} disabled={!!setting} onClick={() => hold([r.p])}>{setting === r.p.projectId ? 'Setting…' : 'Set this date'}</button>}
              </div>
            ))}
          </div>
        </>
      )}
      {!embedded && <div className="aj-pg-row" style={{ marginTop: 14 }}>
        <span />
        <button className="aj-pg-go" onClick={onStart} disabled={data === null}>{startLabel}</button>
      </div>}
    </div>
  )
}

const S = {
  dim: { color: '#8e8e93', fontSize: 13, padding: '12px 0' },
  setAll: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '10px 12px', margin: '4px 0 10px', borderRadius: 10, background: '#fdf3e4', fontSize: 13, color: '#1d1d1f', fontWeight: 600 },
  setBtn: { fontFamily: 'inherit', fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: 0, background: '#07272D', color: '#fff', cursor: 'pointer' },
  tileBtn: { justifySelf: 'start', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 6, border: '0.5px solid #c7c7cc', background: '#fff', color: '#1d1d1f', cursor: 'pointer', marginTop: 2 },
  sum: { display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12.5, fontWeight: 600, margin: '2px 0 10px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))', gap: 10, maxHeight: '62vh', overflowY: 'auto', padding: '2px 4px 2px 2px' },
  tile: { border: '0.5px solid #e5e5ea', borderTop: '3px solid', borderRadius: 10, padding: '12px 14px 12px', display: 'grid', gap: 7, background: '#fff', minWidth: 0 },
  name: { fontSize: 13, fontWeight: 600, color: '#1d1d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  t: { fontFamily: '"DM Mono", monospace', fontSize: 24, lineHeight: 1, color: '#1d1d1f', fontVariantNumeric: 'tabular-nums' },
  small: { fontSize: 10.5, color: '#8e8e93', fontFamily: '"DM Sans", sans-serif' },
  bar: { display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', background: '#ececf0' },
  line: { fontSize: 12, color: '#6e6e73', lineHeight: 1.3 },
  chip: { fontSize: 10.5, fontWeight: 600, border: '0.5px solid', borderRadius: 20, padding: '0 6px', marginRight: 2 },
  sub: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: '#8e8e93', lineHeight: 1.4 },
}
